import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';

const { FiX, FiExternalLink, FiChevronDown, FiChevronUp } = FiIcons;
const { FaRobot } = FaIcons;

const llmOptions = [
  { name: "ChatGPT (OpenAI)", url: "https://chatgpt.com" },
  { name: "Claude (Anthropic)", url: "https://claude.ai" },
  { name: "Copilot (Microsoft)", url: "https://copilot.microsoft.com" },
  { name: "DeepSeek (Hangzhou DeepSeek)", url: "https://www.deepseek.com" },
  { name: "Gemini (Google)", url: "https://gemini.google.com" },
  { name: "Grok (xAI)", url: "https://x.ai" },
  { name: "Le Chat (Mistral AI)", url: "https://chat.mistral.ai" },
  { name: "Llama (Meta)", url: "https://www.meta.ai" },
  { name: "Nova (Amazon.com)", url: "https://nova.amazon.com" },
  { name: "Perplexity (Perplexity AI)", url: "https://www.perplexity.ai" },
  { name: "Qwen (Alibaba Group)", url: "https://qwen.ai" }
];

const LLMSelector = ({ onClose }) => {
  const [selectedLLM, setSelectedLLM] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleLLMSelect = (llm) => {
    setSelectedLLM(llm);
    window.open(llm.url, '_blank');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[100] p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 30 }}
        className="bg-white dark:bg-gray-800 rounded-3xl p-6 sm:p-10 max-w-xl w-full mx-auto shadow-[0_20px_50px_rgba(0,0,0,0.3)] relative flex flex-col max-h-[85vh] border border-gray-100 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-8 flex-shrink-0">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-2xl bg-[#F54CC8] flex items-center justify-center shadow-lg transform -rotate-3">
              <SafeIcon icon={FaRobot} className="w-7 h-7 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight">
                Seleccionar LLM
              </h3>
              <div className="h-1 w-12 bg-[#2B5596] rounded-full mt-1"></div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-all text-gray-400 hover:text-gray-600 dark:hover:text-white"
          >
            <SafeIcon icon={FiX} className="w-7 h-7" />
          </button>
        </div>
        
        <div className="overflow-y-auto pr-2 custom-scrollbar flex-grow">
          <p className="text-gray-600 dark:text-gray-300 text-base mb-8 leading-relaxed">
            El <span className="italic font-medium text-[#2B5596] dark:text-blue-400">prompt</span> ha sido copiado exitosamente. 
            Ahora, selecciona tu modelo de IA preferido para procesar la instrucción:
          </p>

          <div className="relative mb-10">
            <label className="block text-sm font-bold text-gray-400 uppercase tracking-widest mb-3 ml-1">
              Plataforma de IA
            </label>
            <button 
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`w-full flex items-center justify-between p-5 bg-gray-50 dark:bg-gray-900/50 rounded-2xl border-2 transition-all duration-300 group ${dropdownOpen ? 'border-[#2B5596] ring-4 ring-blue-500/10' : 'border-gray-100 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-800'}`}
            >
              <span className={`text-lg font-semibold ${selectedLLM ? selectedLLM.name : 'Elegir una opción...'}`}>
                {selectedLLM ? selectedLLM.name : 'Elegir una opción...'}
              </span>
              <SafeIcon 
                icon={dropdownOpen ? FiChevronUp : FiChevronDown} 
                className={`w-6 h-6 transition-transform duration-300 ${dropdownOpen ? 'text-[#2B5596]' : 'text-gray-400'}`}
              />
            </button>
            
            <AnimatePresence>
              {dropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 15, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 15, scale: 0.98 }}
                  className="absolute left-0 right-0 mt-3 bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 rounded-2xl shadow-[0_15px_40px_rgba(0,0,0,0.15)] z-[110] overflow-hidden"
                >
                  <div className="max-h-[400px] overflow-y-auto llm-dropdown-menu">
                    {llmOptions.map((llm, index) => (
                      <button
                        key={index}
                        className="w-full text-left p-5 hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center justify-between group transition-all border-b last:border-b-0 border-gray-50 dark:border-gray-700"
                        onClick={() => {
                          handleLLMSelect(llm);
                          setDropdownOpen(false);
                        }}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600 group-hover:bg-[#2B5596] transition-colors"></div>
                          <span className="text-lg text-gray-700 dark:text-gray-200 group-hover:text-[#2B5596] dark:group-hover:text-blue-400 font-semibold">
                            {llm.name}
                          </span>
                        </div>
                        <SafeIcon icon={FiExternalLink} className="h-5 w-5 text-gray-300 group-hover:text-[#2B5596] transform group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-700 flex-shrink-0">
          <p className="text-xs text-gray-400 dark:text-gray-500 text-center font-medium">
            Al seleccionar, se abrirá el sitio oficial en una nueva pestaña.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default LLMSelector;